import { z } from "zod";

// Sharing a trip to view (features/sharing.md, "What a viewer sees, and the defaults"): the plan is always shared;
// everything else is a switch, each named here with its default. Shared by the server (what the view's API builds) and
// the web view (Trip settings' switches), so both read one list.

export const SHARES = [
  // Who's in the trip: members' first names, colours and photos, and who's going on what (each member's own only once
  // they've allowed it; the owner's follow this switch).
  "names",
  // Who wants what: each person's tier on a place, People's overlaps (again only once each member has allowed it).
  "ratings",
  // Research findings with their sources; saved links and their previews.
  "research",
  // Friends' recommendations: who recommended a place and their words.
  "recommendations",
  // Notes: members' notes on places, findings and the trip, and an item's own notes.
  "notes",
  // Bookings: whether something is booked or cancelled (without it, a booking reads as chosen).
  "bookings",
  // Booking details: the reference, its link, who booked it, its cancellation policy (only with bookings).
  "booking_details",
  // Money: costs, the budget, what's paid and how, charges, refunds, Money's totals.
  "money",
  // Files: tickets, QR codes and screenshots on items (a document's, with travel documents too).
  "files",
  // The group's own photos (files added as a picture of their item): they often show the people.
  "photos",
  // To-dos: checklists, owners and due dates.
  "todos",
  // Travel documents: only those their owners already share with the whole trip.
  "documents",
] as const;

export type Share = (typeof SHARES)[number];

export const Share = z.enum(SHARES);

// On when sharing is first turned on: what's personal or risky stays out unless the owner says otherwise.
export const DEFAULT_SHARES: readonly Share[] = ["names", "ratings", "research", "bookings"];

// Each switch as Trip settings words it, with a line saying what it covers.
export const SHARE_WORDS: Record<Share, { label: string; covers: string }> = {
  names: {
    label: "Who's in the trip",
    covers: "First names, colours and photos, and who's going on what. Each person's own only once they allow it.",
  },
  ratings: {
    label: "Who wants what",
    covers: "Each person's No, Maybe or Yes. Each person's own only once they allow it.",
  },
  research: { label: "Research and links", covers: "What the AIs found out, with sources, and saved links." },
  recommendations: { label: "Friends' recommendations", covers: "Who recommended a place, and their words." },
  notes: { label: "Notes", covers: "Notes on places and on the trip." },
  bookings: { label: "Bookings", covers: "Whether something is booked, or still to decide." },
  booking_details: {
    label: "Booking details",
    covers:
      "References, booking links, who booked it, cancellation policies. With these, anyone could change a booking.",
  },
  money: { label: "Money", covers: "Prices, the budget, what's paid and how, refunds." },
  files: { label: "Files", covers: "Tickets, QR codes and screenshots." },
  photos: { label: "The group's own photos", covers: "Photos you added of places. They often show people." },
  todos: { label: "To-dos", covers: "Checklists, who they're for and due dates." },
  documents: {
    label: "Travel documents",
    covers: "Only those their owners share with the whole trip.",
  },
};

// The most emails a trip can be shared with.
export const MAX_SHARE_EMAILS = 50;

// The switches as they take effect: Booking details only with Bookings, in the list's own order, each once.
export function effectiveShares(shares: readonly string[]): Set<Share> {
  const on = new Set(SHARES.filter((s) => shares.includes(s)));
  if (!on.has("bookings")) on.delete("booking_details");
  return on;
}

// An email as the list keeps it and compares it: trimmed, lowercased.
export const listEmail = (email: string) => email.trim().toLowerCase();
